<template>
	<div class="relative w-full min-h-[40px]">
		<div v-if="!showButton" class="absolute inset-0">
			<div class="w-full h-full border border-ihh-gray/40 rounded flex items-center justify-center bg-white">
				<Spinner :small="true" class="w-4 h-4" color="border-t-ihh-salmon" />
			</div>
		</div>
		<div :class="{ invisible: !showButton }" class="w-full">
			<button
				@click="processGooglePay"
				id="google-pay-button"
				class="h-10 px-4 items-center w-full bg-black hover:opacity-90 text-white rounded font-medium text-base transition duration-200"
			>
				<div class="flex w-full justify-center items-center gap-2">
					<img src="/images/Google__G__logo.svg" alt="" class="w-4 h-4" />
					<span>Pay</span>
				</div>
			</button>
		</div>
	</div>
</template>

<script>
import helpers from '../../helpers/checkout.js'
import braintree from 'braintree-web'
import Swal from 'sweetalert2'

export default {
	name: 'GooglePayButton',
	inject: ['store', 'checkout'],
	data() {
		return {
			showButton: false,
			paymentsClient: null,
			clientToken: null,
			deviceData: null,
			googlePaymentInstance: null,
			googlePayLoaded: false,
		}
	},

	async mounted() {
		if (this.store?.user?.email !== 'nathan.wikman@outlook.com') {
			this.$emit('updateAvailability', false)
			return
		}
		await this.setup()
	},

	beforeUnmount() {
		// Clean up any resources if needed
	},

	methods: {
		async loadScript() {
			if (!this.googlePayLoaded) {
				return new Promise((resolve, reject) => {
					// Save existing google object properties before loading the script
					const existingGoogleObj = window.google ? { ...window.google } : null

					const script = document.createElement('script')
					script.src = 'https://pay.google.com/gp/p/js/pay.js'
					script.async = true
					script.onload = () => {
						this.googlePayLoaded = true
						console.log('Google Pay script loaded successfully')
						
						// Merge the existing Google properties with the new ones
						if (existingGoogleObj) {
							// Keep the pay object that was just loaded
							const payObj = window.google.payments
							
							// Restore the original google object and add the pay property
							Object.keys(existingGoogleObj).forEach(key => {
								window.google[key] = existingGoogleObj[key]
							})
							
							// Add the payments API
							window.google.payments = payObj
						}
						
						resolve()
					}
					script.onerror = () => {
						console.error('Failed to load Google Pay script')
						reject(new Error('Failed to load Google Pay script'))
					}
					document.head.appendChild(script)
				})
			}
			return Promise.resolve()
		},
		async isAvailable(paymentsClient, clientAuthorization) {
			console.log('Checking Google Pay availability...')
			try {
				const clientInstance = await braintree.client.create({
					authorization: clientAuthorization,
				})

				console.log('Created Braintree Google Pay client instance')

				this.googlePaymentInstance = await braintree.googlePayment.create({
					client: clientInstance,
					googlePayVersion: 2,
					// Google Test Merchant ID; replace with your own in production
					googleMerchantId: 'BCR2DN4TYPLMBMZO', // Optional in sandbox; must be valid for production
				})

				console.log('Created Braintree Google Payment instance')

				// Use a simplified payment method configuration to avoid DataCloneError
				const allowedPaymentMethods = [
					{
						type: 'CARD',
						parameters: {
							allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
							allowedCardNetworks: ['AMEX', 'DISCOVER', 'MASTERCARD', 'VISA'],
						},
					},
				]

				const response = await paymentsClient.isReadyToPay({
					apiVersion: 2,
					apiVersionMinor: 0,
					allowedPaymentMethods: allowedPaymentMethods,
					existingPaymentMethodRequired: true,
				})

				console.log('Checked Google Pay availability')
				console.log('Google Pay availability:', response.result)

				if (response.result) {
					console.log('Google Pay is available')
					return true
				}
				console.log('Google Pay is not available')
				console.log("Payments Client 'isReady' response:", JSON.stringify(response))
				return false
			} catch (err) {
				console.error('Error checking Google Pay availability:', err)
				return false
			}
		},
		async initiateAuthorization(paymentsClient, transactionInfo) {
			if (!this.googlePaymentInstance) {
				this.$emit('updateAvailability', false)
				console.error('Google Payment Instance not initialized. Call isAvailable first.')
				return { success: false, error: 'Google Pay not initialized' }
			}

			try {
				console.log('Google Payment instance available')

				const paymentDataRequest = this.googlePaymentInstance.createPaymentDataRequest({
					transactionInfo: {
						...transactionInfo,
						totalPriceStatus: 'FINAL',
						currencyCode: transactionInfo.currencyCode || 'USD',
					},
				})

				console.log('Created Google Payment Data Request')

				// Configure billing address options
				const cardPaymentMethod = paymentDataRequest.allowedPaymentMethods[0]
				cardPaymentMethod.parameters.billingAddressRequired = true
				cardPaymentMethod.parameters.billingAddressParameters = {
					format: 'FULL',
					phoneNumberRequired: true,
				}

				console.log('Configured billing address options')

				// Avoid logging the full request which may cause issues
				console.log('Payment Data Request prepared')

				const paymentData = await paymentsClient.loadPaymentData(paymentDataRequest)

				console.log('Loaded Google Pay payment data')

				// Avoid logging the full payment data which may contain sensitive information
				console.log('Payment data received')

				const result = await this.googlePaymentInstance.parseResponse(paymentData)

				console.log('Parsed Google Pay payment data')

				return { success: true, ...result } // Contains the payment nonce
			} catch (err) {
				console.error('Error processing Google Pay payment:', err)

				// Handle user cancellation gracefully
				if (err.statusCode === 'CANCELED') {
					return { success: false, canceled: true }
				}

				return { success: false, error: err.message || 'Error processing payment' }
			}
		},
		async setup() {
			try {
				console.log('Loading Google Pay script...')
				await this.loadScript()

				console.log('Creating Payments Client...')
				this.paymentsClient = new google.payments.api.PaymentsClient({
					environment: 'PRODUCTION',
				})

				const { success, token } = await helpers.getClientToken()
				if (!success) {
					console.error('Failed to get braintree client token')
					this.$emit('updateAvailability', false)
					return { result: false }
				}
				this.clientToken = token

				const isAvailable = await this.isAvailable(this.paymentsClient, this.clientToken)
				this.showButton = isAvailable
				this.$emit('updateAvailability', isAvailable)
			} catch (error) {
				console.error('Error setting up Google Pay:', error)
				return { result: false }
			}
		},
		async processGooglePay() {
			try {
				const transactionInfo = {
					currencyCode: 'USD',
					totalPrice: this.grandTotal.toString(),
				}

				const result = await this.initiateAuthorization(this.paymentsClient, transactionInfo)
				console.log('Process Google Pay result:', result)
				console.log('Stringify: ', JSON.stringify(result))

				if (result.success && result.nonce) {
					// Complete the order with the payment nonce
					console.log('Payment nonce received:', result.nonce)
					// await this.completeOrder(result.nonce)
				} else if (result.canceled) {
					console.log('User canceled the Google Pay payment')
				} else {
					Swal.fire({
						icon: 'error',
						title: 'Google Pay Error',
						text: result.error || 'There was an error processing your Google Pay payment.',
						confirmButtonText: 'OK',
					})
				}
			} catch (error) {
				console.error('Error in processGooglePay:', error)
				Swal.fire({
					icon: 'error',
					title: 'Google Pay Error',
					text: error.message || 'There was an error processing your Google Pay payment.',
					confirmButtonText: 'OK',
				})
			}
		},
		async completeOrder(nonce) {
			try {
				// Create order in BigCommerce
				const bcOrderResponse = await helpers.placeOrder({ paymentMethod: 'apm' })

				const bcOrderId = bcOrderResponse?.bcOrderId
				if (!bcOrderId) {
					throw new Error('Failed to create BigCommerce order.')
				}

				// Process payment with Braintree
				const paymentResponse = await helpers.processBraintreePayment({
					nonce,
					amount: this.grandTotal,
					bcOrderId,
					deviceData: this.deviceData,
				})

				if (!paymentResponse?.success) {
					throw new Error(paymentResponse?.message || 'Payment processing failed.')
				}

				console.log('Payment successful:', paymentResponse.data)
				const { redirectUrl } = await helpers.handleCheckoutSuccess(bcOrderId, paymentResponse, ['submitted_for_settlement', 'settling', 'settled'])

				this.$router.push(redirectUrl)
			} catch (error) {
				console.error('Error completing Google Pay order:', error)
				Swal.fire({
					icon: 'error',
					title: 'Error',
					text: error.message || 'There was an error completing your order. Please try again.',
					confirmButtonText: 'OK',
				})
			}
		},
	},

	computed: {
		subTotal() {
			return Number(parseFloat(this.store?.cart?.total).toFixed(2))
		},
		subTotalWithDiscounts() {
			// coupon discounts are already applied to the cart subTotal
			const total = this.subTotal - this.checkout.giftCertificateAmount - this.checkout.storeCredit
			return Number(parseFloat(total).toFixed(2))
		},
		grandTotal() {
			let total = this.subTotalWithDiscounts + (this.checkout.shipping.selectedShipping?.cost || 0)
			if (total <= 0) total = this.subTotal
			return Number(parseFloat(total).toFixed(2))
		},
	},
}
</script>

<style scoped>
#google-pay-button {
	width: 100%;
	height: 40px;
}

#google-pay-button > div {
	width: 100% !important;
	height: 40px !important;
}

#google-pay-button iframe {
	height: 40px !important;
}
</style>
